import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { updatePrestataire, createPrestataire, emptyPrestataire } from './prestatairesSlice'
import { Input } from '@/components/ui'
import AddressFields from '@/components/AddressFields'
import { cancelNewPrestataireCreation } from '../interventions/interventionsSlice'

import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'

export default ({ 
  onClose = () => {}, onSuccess = () => {},
  onPrestataireCreated
}) => {
  const dispatch = useDispatch()
  const prestataire = useSelector((state) => state.prestataires.current || emptyPrestataire)
  const errors = useSelector((state) => state.prestataires.requestStatus.errors)
  const isCreating = useSelector((state) => state.prestataires.loadingStatus.create)
  const isUpdating = useSelector((state) => state.prestataires.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleSubmit, 
    setValue, getValues, watch,
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: prestataire,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })


  // const onReset = () => {
  //   reset(prestataire)
  // }

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    // onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque visite change 
  useEffect(() => {
    if (!prestataire) return;
    Object.keys(prestataire).forEach( key => {
      if(key in prestataire) {
        setValue(key, prestataire[key])
      }
    })

    // useFormConfirmation feature
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)
  }, [prestataire]) 

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, isSubmitting, onSuccess, dispatch])

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le bien existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (prestataire.id) {
        await dispatch(updatePrestataire({id: prestataire.id, data: formData}))
      } else {
        const result = await dispatch(createPrestataire({...formData})).unwrap()
        if(onPrestataireCreated && result && result.id) {
          onPrestataireCreated(result)
        }
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {prestataire.id ? 'Modifier le prestataire' : 'Ajouter un prestataire'}
          </h2>
        </div>

        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 max-h-[calc(80vh-150px)] overflow-y-auto">
          <div className="card-body p-4">
            <div className="grid grid-cols-3 gap-2 p-4">

              <div className="flex flex-col items-center gap-4">                
                <Input.Text control={control} label='Nom' name='nom' 
                  rules={{required: 'Le nom doit être renseigné'}}
                  validator={{hasError, getErrorMessage}}
                  className="w-2/3"
                />
                <Input.Text control={control} label='Prénom' name='prenom' 
                  className="w-2/3"
                />
                <Input.Email control={control} name='email' 
                  rules={{required: "L'email doit être renseigné"}}
                  validator={{hasError, getErrorMessage}}
                  className="w-2/3"
                />
                <Input.PhoneNumber control={control} 
                  name='telephone'
                  validator={{hasError, getErrorMessage}}
                  className="w-2/3"
                />
              </div>

              <div className="flex flex-col items-center gap-4">                
                <AddressFields control={control} 
                  errors={formErrors} apiErrors={errors}
                  without={['pays']}
                />
              </div>
            
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleFormCancel}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
      <FormConfirmDialog/>
    </div>
  )
}